import * as React from 'react';
import { useEffect } from 'react';
import Tree from 'react-d3-tree';
import { useState, useMemo } from "react";
import { 
  CircularProgress, 
  Box, 
  Typography, 
  Modal, 
  Button,
  Avatar,
  Grid,
  Divider,
  IconButton,
  Breadcrumbs,
  Link,
  TextField,
  InputAdornment,
  Paper
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AdminDashboard from './AdminDashboard';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import HomeIcon from '@mui/icons-material/Home';
import SearchIcon from '@mui/icons-material/Search';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';

const transformData = (node, nodesMap, visited = new Set()) => {
  // Check for null/undefined or if we've already visited this node
  if (!node || !nodesMap || visited.has(node.ReferralID)) {
    return null;
  }

  // Mark this node as visited
  visited.add(node.ReferralID);

  // Create the transformed node
  const transformedNode = {
    name: `${node.ReferralID}\n${node.FullName}\n${node.VendorType}`,
    attributes: {
      id: node.ReferralID
    },
    children: []
  };

  // Process children only if they exist and haven't been visited
  if (node.FrontMoonID && nodesMap[node.FrontMoonID]) {
    const moonChild = transformData(nodesMap[node.FrontMoonID], nodesMap, new Set(visited));
    if (moonChild) {
      transformedNode.children.push(moonChild);
    }
  }

  if (node.FrontSunID && nodesMap[node.FrontSunID]) {
    const sunChild = transformData(nodesMap[node.FrontSunID], nodesMap, new Set(visited));
    if (sunChild) {
      transformedNode.children.push(sunChild);
    }
  }

  return transformedNode;
};

export default function Genealogy() {
  const theme = useTheme();
  const [selectedNode, setSelectedNode] = useState(null);
  const [currentRoot, setCurrentRoot] = useState(null);
  const [nodePath, setNodePath] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  const userData = JSON.parse(sessionStorage.getItem("admin_user"));
  const { UserID, AuthCode } = userData?.BoAcntLst?.[0] || {};

  const CustomNode = ({ nodeDatum, toggleNode, theme, nodesMap, onNodeClick }) => {
    const isCurrentRoot = currentRoot?.ReferralID === nodeDatum.attributes?.id;
    const [isHovered, setIsHovered] = useState(false);

    const referralId = nodeDatum.attributes?.id;
    const nodeData = nodesMap?.[referralId];

    const handleClick = (e) => {
      e.stopPropagation();
      if (nodeData) {
        onNodeClick(nodeData);
      }
    };
    
    return (
      <g
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Node background */}
        <rect
          x="-100"
          y="-40"
          width="225"
          height="80"
          rx="15"
          fill={isCurrentRoot ? "#299cdb" : theme.palette.mode === 'dark' ? '#424242' : '#ffffff'}
          stroke={isHovered ? "#299cdb" : isCurrentRoot ? "#1a73e8" : "#e0e0e0"}
          strokeWidth={isHovered ? 2 : 1}
          filter={isHovered ? "url(#shadow)" : ""}
        />

        {/* Profile image */}
        <image
          href={nodeData?.RefImage || 'https://static.vecteezy.com/system/resources/previews/009/292/244/non_2x/default-avatar-icon-of-social-media-user-vector.jpg'}
          x="-85"
          y="-25"
          width="50"
          height="50"
          clipPath="circle(25px at 25px 25px)"
          style={{
            borderRadius: '50%',
            filter: 'url(#image-shadow)',
            cursor: 'pointer'
          }}
        />
        
        {/* Node content */}
        <foreignObject x="-20" y="-30" width="130" height="60">
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
            color: isCurrentRoot ? '#fff' : theme.palette.text.primary,
            fontSize: '14px',
            lineHeight: '1.4'
          }}>
            <div style={{ 
              fontWeight: 600, 
              fontSize: '12px',
              color: isCurrentRoot ? '#fff' : '#299cdb',
              marginBottom: '4px'
            }}>
              {nodeDatum.name.split('\n')[0]}
            </div>
            <div style={{ fontWeight: 500, fontSize: '14px' }}>
              {nodeDatum.name.split('\n')[1]}
            </div>
            <div style={{ 
              fontSize: '12px',
              color: isCurrentRoot ? '#e0e0e0' : '#757575'
            }}>
              {nodeDatum.name.split('\n')[2]}
            </div>
          </div>
        </foreignObject>
  
        {/* Connection line decoration */}
        {!isCurrentRoot && (
          <path
            d="M0,-40 V-50"
            stroke={theme.palette.mode === 'dark' ? '#616161' : '#e0e0e0'}
            strokeWidth="1.5"
          />
        )}
      </g>
    );
  };
  
  const svgDefs = (
    <defs>
      <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.2)" />
      </filter>
    </defs>
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTreeData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch('https://testing.esnep.com/magnessa/api/bo/genealogy', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ComID: 'EH25', Flag: 'S', UserID: UserID, AuthCode: AuthCode }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        
        if (result.StatusCode === 200) {
          if (result.BoProdLst && result.BoProdLst.length > 0) {
            setData(result.BoProdLst);
            const rootNode = result.BoProdLst.find(node => node.VendorType === 'Root');
            if (rootNode) {
              setCurrentRoot(rootNode);
              setNodePath([rootNode]);
            } else {
              setError('Root node not found in the data');
            }
          } else {
            setData([]); // Explicitly set empty array
          }
        } else {
          throw new Error(result.Message || 'Failed to fetch genealogy data');
        }
      } catch (err) {
        setError(err.message || 'An unknown error occurred');
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    if (UserID && AuthCode) {
      fetchTreeData();
    } else {
      setError('User authentication data not found');
      setLoading(false);
    }
  }, [UserID, AuthCode]);

  const nodesMap = useMemo(() => {
    if (!data || !Array.isArray(data)) return {};
    return data.reduce((map, node) => {
      if (node?.ReferralID) {
        map[node.ReferralID] = node;
      }
      return map;
    }, {});
  }, [data]);

  const handleNodeClick = (node) => {
    if (!node) return;
    setSelectedNode(node);
    setShowSearchResults(false);
  };

  const handleGoUp = () => {
    if (nodePath.length > 1) {
      const newPath = [...nodePath];
      newPath.pop();
      const parentNode = newPath[newPath.length - 1];
      if (parentNode) {
        setCurrentRoot(parentNode);
        setNodePath(newPath);
      }
    }
    setShowSearchResults(false);
  };

  const handleGoToRoot = () => {
    if (!data) return;
    const rootNode = data.find(node => node.VendorType === 'Root');
    if (rootNode) {
      setCurrentRoot(rootNode);
      setNodePath([rootNode]);
    }
    setShowSearchResults(false);
  };

  const handleSearch = () => {
    const term = searchTerm.trim();
    
    if (!term || !data) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const results = data.filter(node => 
      node.ReferralID.toLowerCase().includes(term.toLowerCase()) || 
      (node.FullName && node.FullName.toLowerCase().includes(term.toLowerCase()))
    );

    setSearchResults(results);
    setShowSearchResults(results.length > 0);
  };

  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  }, [searchTerm]);

  const handleSearchResultClick = (node) => {
    setCurrentRoot(node);
    setNodePath([node]);
    setSelectedNode(null);
    setShowSearchResults(false);
    setSearchTerm('');
  };

  const handleCloseModal = (e) => {
    e.stopPropagation();
    setSelectedNode(null);
  };

  const handleViewDownline = (e) => {
    e.stopPropagation();
    if (selectedNode) {
      setCurrentRoot(selectedNode);
      setNodePath([...nodePath, selectedNode]);
    }
    setSelectedNode(null);
  };

  const DetailItem = ({ label, value, color, isNumeric = false }) => (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
        {label}
      </Typography>
      <Typography 
        variant="body2" 
        sx={{ 
          fontWeight: 500,
          color: color || 'text.primary',
          wordBreak: 'break-word'
        }}
      >
        {value === null || value === undefined
          ? (isNumeric ? '0' : '-')
          : value}
      </Typography>
    </Box>
  );

  if (loading) {
    return (
      <AdminDashboard>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <CircularProgress size={60} />
        </Box>
      </AdminDashboard>
    );
  }

  if (error) {
    return (
      <AdminDashboard>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '80vh',
          textAlign: 'center',
          p: 3
        }}>
          <ErrorOutlineIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h5" color="error" gutterBottom>
            Error Loading Data
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, maxWidth: '600px' }}>
            {error}
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => window.location.reload()}
            startIcon={<HomeIcon />}
            sx={{ mt: 2 }}
          >
            Refresh Page
          </Button>
        </Box>
      </AdminDashboard>
    );
  }

  if (!data || data.length === 0) {
    return (
      <AdminDashboard>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '80vh',
          textAlign: 'center',
          p: 3
        }}>
          <SentimentDissatisfiedIcon color="action" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            No Genealogy Data Available
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, maxWidth: '600px' }}>
            The genealogy tree is currently empty. Please check back later or contact support if this is unexpected.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="outlined" 
              onClick={() => window.location.reload()}
              sx={{ mt: 2 }}
            >
              Refresh
            </Button>
            <Button 
              variant="contained" 
              onClick={() => {/* Add contact support function here */}}
              sx={{ mt: 2 }}
            >
              Contact Support
            </Button>
          </Box>
        </Box>
      </AdminDashboard>
    );
  }

  if (!currentRoot) {
    return (
      <AdminDashboard>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '80vh',
          textAlign: 'center',
          p: 3
        }}>
          <ErrorOutlineIcon color="warning" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Root Node Not Found
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, maxWidth: '600px' }}>
            We couldn't identify the root node in the genealogy data. This might indicate a data structure issue.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="outlined" 
              onClick={() => window.location.reload()}
              sx={{ mt: 2 }}
            >
              Try Again
            </Button>
            <Button 
              variant="contained" 
              onClick={() => handleGoToRoot()}
              sx={{ mt: 2 }}
            >
              Find Root Node
            </Button>
          </Box>
        </Box>
      </AdminDashboard>
    );
  }

  const treeData = transformData(currentRoot, nodesMap);

  return (
    <AdminDashboard>
      <Box component="main" sx={{ 
        flexGrow: 1,
        p: 3,
        backgroundColor: "#f7f7f7",
        minHeight: '100vh'
      }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" fontWeight={600}>
            Genealogy Tree
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{lineHeight:"2"}}>
            Manage and view the data of the specific user.
          </Typography>
          
          {/* Search Box */}
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 2, gap: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by ID or Name"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                if (!e.target.value.trim()) {
                  setSearchResults([]);
                  setShowSearchResults(false);
                }
              }}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                maxWidth: 400,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '50px',
                  backgroundColor: theme.palette.background.paper,
                }
              }}
            />
            <Button 
              variant="contained" 
              onClick={handleSearch}
              sx={{ borderRadius: '50px' }}
            >
              Search
            </Button>

            {/* Navigation controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
              <IconButton 
                onClick={handleGoUp} 
                disabled={nodePath.length <= 1}
                sx={{ mr: 1 }}
              >
                <ArrowUpwardIcon />
              </IconButton>
              
              <IconButton 
                onClick={handleGoToRoot}
                sx={{ mr: 2 }}
              >
                <HomeIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Search Results */}
          {showSearchResults && (
            <Paper sx={{ 
              position: 'absolute', 
              zIndex: 1, 
              width: '400px', 
              maxHeight: '300px', 
              overflow: 'auto',
              boxShadow: theme.shadows[3],
              borderRadius: 1,
              mt: -2
            }}>
              {searchResults.length > 0 ? (
                searchResults.map((node) => (
                  <Box 
                    key={node.ReferralID}
                    sx={{ 
                      p: 2, 
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSearchResultClick(node);
                    }}
                  >
                    <Typography fontWeight={600}>{node.FullName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      ID: {node.ReferralID} | Type: {node.VendorType}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Box sx={{ p: 2 }}>
                  <Typography>No results found</Typography>
                </Box>
              )}
            </Paper>
          )}

          {/* Breadcrumbs */}
          <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 2 }}>
            {nodePath.map((node, index) => (
              <Link
                key={node?.ReferralID || index}
                color={index === nodePath.length - 1 ? 'text.primary' : 'inherit'}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (node) {
                    setCurrentRoot(node);
                    setNodePath(nodePath.slice(0, index + 1));
                  }
                }}
              >
                {node?.FullName || 'Unknown'}
              </Link>
            ))}
          </Breadcrumbs>
        </Box>
        
        <Box sx={{ width: "100%", height: "200vh", display: "flex" }}>
          {treeData && (
            <Tree
              data={treeData}
              orientation="vertical"
              pathFunc="step"
              translate={{ x: window.innerWidth / 2.5, y: 40 }}
              nodeSize={{ x: 185, y: 140 }}
              separation={{ siblings: 1.5, nonSiblings: 1.5 }}
              renderCustomNodeElement={(rd3tProps) => (
                <CustomNode 
                  {...rd3tProps} 
                  theme={theme}
                  nodesMap={nodesMap}
                  onNodeClick={handleNodeClick}
                />
              )}
              styles={{
                links: {
                  stroke: theme.palette.mode === 'dark' ? '#616161' : '#e0e0e0',
                  strokeWidth: 1.5,
                }
              }}
            >
              {svgDefs}
            </Tree>
          )}

          <Modal
            open={Boolean(selectedNode)}
            onClose={handleCloseModal}
            aria-labelledby="node-details-modal"
          >
            <Box 
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 600,
                bgcolor: 'background.paper',
                boxShadow: 24,
                borderRadius: 3,
                overflow: 'hidden',
                outline: 'none'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {selectedNode && (
                <>
                  <Box sx={{
                    background: theme.palette.primary.main,
                    p: { xs: 2, sm: 3 },
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    maxWidth: '100%',
                    mx: 'auto',
                    '&:after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -20,
                      right: -20,
                      width: 80,
                      height: 80,
                      background: 'rgba(255,255,255,0.1)',
                      borderRadius: '50%'
                    }
                  }}>
                    <Avatar
                      src={selectedNode.RefImage}
                      sx={{
                        width: { xs: 60, sm: 80 },
                        height: { xs: 60, sm: 80 },
                        margin: '0 auto 16px',
                        border: '3px solid white',
                        transition: 'all 0.3s',
                        '&:hover': {
                          transform: { xs: 'scale(1.05)', sm: 'scale(1.1)' }
                        }
                      }}
                    />
                    <Typography 
                      variant="h6" 
                      noWrap
                      sx={{ 
                        color: 'white', 
                        mb: 0.5,
                        fontSize: { xs: '1.1rem', sm: '1.25rem' },
                        maxWidth: '100%',
                        mx: 'auto',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        px: 1
                      }}
                    >
                      {selectedNode.FullName}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'rgba(255,255,255,0.8)',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        maxWidth: '95%',
                        mx: 'auto',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: 1.3,
                        px: 1
                      }}
                    >
                      {selectedNode.ReferralID}
                    </Typography>
                  </Box>

                  <Box sx={{ p: 3 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <DetailItem label="Vendor Type" value={selectedNode.VendorType} />
                        <DetailItem label="Sun Count" value={selectedNode.SunCount} isNumeric />
                        <DetailItem label="Moon Count" value={selectedNode.MoonCount} isNumeric />
                      </Grid>
                      <Grid item xs={6}>
                        <DetailItem label="Referral Via" value={selectedNode.ReferralVia} />
                        <DetailItem label="Parent ID" value={selectedNode.ParentID} />
                        <DetailItem label="Status" value="Active" color="success.main" />
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Button
                        variant="outlined"
                        onClick={handleViewDownline}
                      >
                        View Downline
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleCloseModal}
                        sx={{
                          bgcolor: 'primary.main',
                          '&:hover': { bgcolor: 'primary.dark' }
                        }}
                      >
                        Close
                      </Button>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </Modal>
        </Box>
      </Box>
    </AdminDashboard>
  );
}